import { useState } from 'react';
import type { ChangeEvent } from 'react';

import Button from '../Button';
import Flex from '../Flex';
import RadioButton from '../RadioButton';
import RadioButtonGroup from '../RadioButtonGroup';
import Text from '../Text';
import TextArea from '../TextArea';
import { useI18n, useTestIds } from '../../hooks';

import type { AIRewriteProps, AIAction } from './AIRewrite.types';
import { getAIRewriteTestIds } from './AIRewrite.test-ids';
import {
  StyledAIRewriteDialog,
  StyledFormSection,
  StyledInstructionsWrapper
} from './AIRewrite.styles';

const AIRewrite = ({
  target,
  testId,
  onGenerate,
  onSubmit,
  onCancel,
  isGenerating,
  suggestion,
  suggestionEditor
}: AIRewriteProps) => {
  const t = useI18n();
  const testIds = useTestIds(testId, getAIRewriteTestIds);
  const [selectedAction, setSelectedAction] = useState<AIAction>('improve');
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  const handleActionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newAction = e.target.value;
    if (
      newAction === 'improve' ||
      newAction === 'shorter' ||
      newAction === 'professional' ||
      newAction === 'friendly'
    ) {
      setSelectedAction(newAction);
    }
  };

  const handleGenerate = () => {
    onGenerate(selectedAction, additionalInstructions);
  };

  const handleCancel = () => {
    target.focus();
    onCancel();
  };

  return (
    <StyledAIRewriteDialog
      target={target}
      heading={t('ai_rewrite_title')}
      onCancel={handleCancel}
      onSubmit={{
        disabled: !suggestion?.trim() || isGenerating,
        handler: onSubmit
      }}
    >
      <Flex container={{ direction: 'column', rowGap: 2 }}>
        <StyledFormSection>
          <Flex container={{ direction: 'column', rowGap: 2 }}>
            <RadioButtonGroup name='aiAction' label={t('ai_rewrite_label')} required inline>
              <RadioButton
                value='improve'
                label={t('ai_rewrite_improve')}
                checked={selectedAction === 'improve'}
                onChange={handleActionChange}
                data-testid={testIds.radioClearer}
              />
              <RadioButton
                value='shorter'
                label={t('ai_rewrite_shorter')}
                checked={selectedAction === 'shorter'}
                onChange={handleActionChange}
                data-testid={testIds.radioShorter}
              />
              <RadioButton
                value='professional'
                label={t('ai_rewrite_professional')}
                checked={selectedAction === 'professional'}
                onChange={handleActionChange}
                data-testid={testIds.radioProfessional}
              />
              <RadioButton
                value='friendly'
                label={t('ai_rewrite_friendly')}
                checked={selectedAction === 'friendly'}
                onChange={handleActionChange}
                data-testid={testIds.radioFriendly}
              />
            </RadioButtonGroup>

            <StyledInstructionsWrapper>
              <TextArea
                label={t('ai_rewrite_instructions_label')}
                info={t('ai_rewrite_instructions_info')}
                value={additionalInstructions}
                onChange={e => setAdditionalInstructions(e.target.value)}
                maxLength={200}
                rows={1}
                autoResize={false}
                data-testid={testIds.instructionsInput}
              />
            </StyledInstructionsWrapper>
            <div>
              <Button
                type='button'
                onClick={handleGenerate}
                disabled={isGenerating}
                data-testid={testIds.generateButton}
              >
                {t('ai_rewrite_generate')}
              </Button>
            </div>
          </Flex>
        </StyledFormSection>

        <Flex container={{ direction: 'column', rowGap: 0.5 }}>
          <Text variant='h4'>{t('ai_rewrite_result_label')}</Text>
          {suggestionEditor ?? (
            <TextArea
              label={t('ai_rewrite_result_label')}
              labelHidden
              value={suggestion ?? ''}
              readOnly
              disabled={!suggestion}
            />
          )}
        </Flex>
      </Flex>
    </StyledAIRewriteDialog>
  );
};

export default AIRewrite;
